import { Spacing } from '@persona-id/design-tokens';
import { EditIcon } from '@persona-id/design-tokens/icons';
import {
  cloneElement,
  KeyboardEventHandler,
  memo,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import Input, { InputProps } from 'design-system/components/Input';
import { paragraphMixins } from 'design-system/mixins/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';
import { useFormSecurely as useForm } from 'lib/security/reactHookForm';

const DisplayWrapper = styled.div<{ editable: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${(props) =>
    props.editable &&
    css`
      cursor: pointer;
      &:hover {
        transition-duration: ${TransitionDuration.Medium};
        svg {
          display: block;
        }
      }
    `}
`;

export const StyledInput = styled(Input)`
  height: ${({ theme }) => theme.element.height.sm};
  ${paragraphMixins.md};
`;

export const StyledWrapper = styled.div`
  min-height: max-content;
`;

const DefaultPreviewContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const StyledEditIconContainer = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  height: ${Spacing[4]};
  width: ${Spacing[4]};
  margin-right: ${Spacing[1]};
`;

const StyledEditIcon = styled(EditIcon)`
  max-width: ${Spacing[4]};
  color: ${({ theme }) => theme.color.variants.basic.highlightDark};
`;

export interface InputWithHoverEditButtonFieldProps
  extends Omit<InputProps, 'onBlur' | 'onClear' | 'rightButton'> {
  fieldName: string;
  displayElement?: JSX.Element;
  displayPlaceholder?: string;
  doubleClickToEdit?: boolean;
  iconVisible?: boolean;
  onEditButtonClick?: () => void;
  onClick?: (e: ReactMouseEvent<HTMLElement, MouseEvent>) => void;
  onBlur?: (value: string) => void;
}

const InputWithHoverEditButtonField = ({
  id,
  className,
  fieldName,
  displayElement,
  displayPlaceholder,
  placeholder,
  autoFocus,
  disabled,
  doubleClickToEdit,
  iconVisible,
  onEditButtonClick,
  onBlur,
  onClick,
  ...inputProps
}: InputWithHoverEditButtonFieldProps) => {
  const [editing, { enable: enableEditing, disable: disableEditing }] = useToggle();
  const [editButtonDisplayed, { enable: showEditButton, disable: hideEditButton }] = useToggle();
  const [placeholderValue, setPlaceholderValue] = useState(placeholder);

  const {
    register,
    formState: { isDirty },
    getValues,
    reset,
  } = useForm({ defaultValues: { [fieldName]: '' } });

  useEffect(() => {
    setPlaceholderValue(placeholder);
  }, [placeholder]);

  const handleBlur = useCallback(() => {
    const newValue = getValues(fieldName);
    onEditButtonClick?.();
    if (!isDirty || newValue === '') return false;
    setPlaceholderValue(newValue);
    onBlur?.(newValue);
    // reset the form state so that the input
    // is empty on every focus if the value has changed
    reset();
  }, [onBlur, isDirty, getValues, fieldName, onEditButtonClick, reset]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (['Enter', 'Escape', 'Tab'].includes(e.key)) {
      e.preventDefault();
      handleBlur();
      disableEditing();
    }
  };

  const enableEditCallback = useCallback(() => {
    if (disabled) return false;
    enableEditing();
    onEditButtonClick?.();
  }, [disabled, enableEditing, onEditButtonClick]);

  return (
    <>
      <StyledWrapper
        id='input-hover-edit-button__styled-wrapper'
        data-test='input-hover-edit-button__styled-wrapper'
        className={className}
        onClick={(e: ReactMouseEvent<HTMLElement, MouseEvent>) => {
          e.preventDefault();
          e.nativeEvent.stopPropagation();

          if (doubleClickToEdit && e.nativeEvent.detail === 1) {
            return false;
          }
          // double click to enable editing on the input
          onClick?.(e);
          enableEditCallback();
        }}
        onMouseEnter={(e) => {
          e.stopPropagation();
          showEditButton();
        }}
        onMouseLeave={(e) => {
          e.stopPropagation();
          hideEditButton();
        }}
      >
        {editing ? (
          <StyledInput
            id={`input-hover-edit-button__${id}`}
            {...inputProps}
            {...register(fieldName)}
            placeholder={placeholderValue}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              handleBlur();
              disableEditing();
            }}
            autoFocus
          />
        ) : (
          <DisplayWrapper editable={!disabled}>
            {
              // clone element for render style, but centralize content display
              cloneElement(
                displayElement ?? <DefaultPreviewContainer />,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                null,
                displayPlaceholder ?? placeholderValue,
              )
            }
            <StyledEditIconContainer
              id='input-hover-edit-button__icon-container'
              data-test='input-hover-edit-button__icon-container'
              onClick={(e: ReactMouseEvent<HTMLElement, MouseEvent>) => {
                if (disabled) {
                  return false;
                }
                e.stopPropagation();
                onClick?.(e);
                enableEditCallback();
              }}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              visible={(editButtonDisplayed || iconVisible || inputProps.active) && !disabled}
            >
              <StyledEditIcon />
            </StyledEditIconContainer>
          </DisplayWrapper>
        )}
      </StyledWrapper>
    </>
  );
};

export default memo(InputWithHoverEditButtonField);
